import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Music, Video, MessageSquare, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import AdminLayout from '@/components/admin/AdminLayout';
import { useSite } from '@/context/SiteContext';

const AdminDashboard = () => {
  const { playlists, videos } = useSite();

  const stats = [
    { 
      icon: Music, 
      label: 'Music Albums', 
      value: playlists.length,
      link: '/admin/playlists',
      description: 'Manage your music collection'
    },
    { 
      icon: Video, 
      label: 'Videos', 
      value: videos.length,
      link: '/admin/videos',
      description: 'Manage video content'
    },
    { 
      icon: FileText, 
      label: 'Site Content', 
      value: '—',
      link: '/admin/content',
      description: 'Edit text, images, and bio'
    },
    { 
      icon: MessageSquare, 
      label: 'Messages', 
      value: '—',
      link: '/admin/messages',
      description: 'View contact form submissions'
    },
  ];

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-8">
        {/* Welcome */}
        <div>
          <h2 className="font-serif text-3xl mb-2">Welcome back, Justin</h2>
          <p className="text-muted-foreground">
            Manage your website content from this dashboard.
          </p>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat) => (
            <Link key={stat.link} to={stat.link}>
              <Card className="bg-card border-border hover:border-muted-foreground/30 transition-colors h-full">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <stat.icon className="h-5 w-5 text-muted-foreground" />
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-serif mb-1">{stat.value}</p>
                  <p className="font-medium text-sm">{stat.label}</p>
                  <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Quick actions */}
        <div>
          <h3 className="font-serif text-xl mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/admin/content">
              <Card className="bg-card border-border hover:border-muted-foreground/30 transition-colors cursor-pointer">
                <CardContent className="p-6">
                  <FileText className="h-8 w-8 mb-4 text-muted-foreground" />
                  <h4 className="font-medium mb-1">Edit Bio & Content</h4>
                  <p className="text-sm text-muted-foreground">
                    Update your about section, hero text, and images
                  </p>
                </CardContent>
              </Card>
            </Link>
            
            <Link to="/admin/playlists">
              <Card className="bg-card border-border hover:border-muted-foreground/30 transition-colors cursor-pointer">
                <CardContent className="p-6">
                  <Music className="h-8 w-8 mb-4 text-muted-foreground" />
                  <h4 className="font-medium mb-1">Add Music</h4>
                  <p className="text-sm text-muted-foreground">
                    Create new playlists and add tracks
                  </p>
                </CardContent>
              </Card>
            </Link>
            
            <Link to="/admin/videos">
              <Card className="bg-card border-border hover:border-muted-foreground/30 transition-colors cursor-pointer">
                <CardContent className="p-6">
                  <Video className="h-8 w-8 mb-4 text-muted-foreground" />
                  <h4 className="font-medium mb-1">Add Video</h4>
                  <p className="text-sm text-muted-foreground">
                    Upload videos and performances
                  </p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
